import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n, useT } from "@/i18n/I18nContext";
import { api, formatApiError } from "@/lib/api";

const MAX_SECONDS = 60;

/**
 * "Tap to speak" recorder. Captures up to 60 s of mic audio, POSTs the blob to
 * /api/voice/transcribe, and invokes onTranscribed(text).
 */
export default function VoiceRecorder({ onTranscribed }) {
  const t = useT();
  const { lang } = useI18n();
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recRef.current && recRef.current.state !== "inactive") {
        try {
          recRef.current.stop();
        } catch {
          /* ignore */
        }
      }
    },
    [],
  );

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleStart = async () => {
    if (state !== "idle") return;
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error(t("voice.permission_denied"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stopTimer();
        stream.getTracks().forEach((tr) => tr.stop());
        setState("transcribing");
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          if (blob.size < 1000) {
            toast.error(t("voice.error"));
            setState("idle");
            setSecs(0);
            return;
          }
          const fd = new FormData();
          fd.append("file", blob, "intake.webm");
          fd.append("language", lang || "en");
          const { data } = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data?.text) {
            onTranscribed(data.text);
            toast.success(t("voice.transcribed"));
          } else {
            toast.error(t("voice.error"));
          }
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || t("voice.error"));
        } finally {
          setState("idle");
          setSecs(0);
        }
      };
      rec.start();
      setState("recording");
      setSecs(0);
      timerRef.current = setInterval(() => {
        setSecs((s) => {
          const next = s + 1;
          if (next >= MAX_SECONDS) {
            try {
              rec.stop();
            } catch {
              /* ignore */
            }
          }
          return next;
        });
      }, 1000);
    } catch {
      toast.error(t("voice.permission_denied"));
    }
  };

  const handleStop = () => {
    if (state !== "recording" || !recRef.current) return;
    try {
      recRef.current.stop();
    } catch {
      /* ignore */
    }
  };

  if (state === "recording") {
    return (
      <button
        type="button"
        onClick={handleStop}
        className="mt-2 inline-flex items-center gap-2 rounded-lg border-2 border-[#E85D04] bg-[#fff5e6] px-4 py-2 text-sm font-bold text-[#E85D04] hover:bg-[#fee6c8]"
        data-testid="voice-stop"
      >
        <Square className="h-3.5 w-3.5 animate-pulse" /> {t("voice.recording")} · {secs}s
      </button>
    );
  }
  if (state === "transcribing") {
    return (
      <div
        className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[#E0E4E2] bg-[#FDFBF7] px-4 py-2 text-sm font-bold text-[#5C6B68]"
        data-testid="voice-transcribing"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("voice.transcribing")}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={handleStart}
      className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[#E0E4E2] bg-white px-4 py-2 text-sm font-semibold text-[#11322A] transition hover:border-[#11322A] hover:bg-[#FDFBF7]"
      data-testid="voice-start"
    >
      <Mic className="h-3.5 w-3.5" /> {t("voice.intake.cta")}
    </button>
  );
}
